import type { BibleState } from './guide-state';

/** Single step in the guided tour */
export interface TourStep {
  title: string;
  description: string;
  highlightSelector?: string;
  action?: (state: BibleState) => void;
  isReady?: (state: BibleState) => boolean;
  /** Step works inside the Groups & Students modal — it renders scrimless
   *  so the tour card stays visible and clickable beside it. */
  usesStudySettings?: boolean;
}

/**
 * Create tour steps with access to bible state for side effects.
 * Factory pattern avoids circular imports — steps reference state lazily.
 */
export function createTourSteps(): TourStep[] {
  return [
    {
      title: 'Welcome to Studies',
      description:
        'SKJV has a very lightweight, structured way for you to study the Word with groups.\n\n' +
        "Let's create a real Bible study together — John 1:1-5. " +
        'This guide will walk you through each step. You can reopen it ' +
        'anytime from the ? button in the top right.',
    },
    {
      title: 'Create a Study Group',
      description:
        'First, you need a study group. Groups hold your students and teachers. ' +
        'Tap the gear button at the top of the study panel — it opens Groups & ' +
        'Students — then tap "Create new group" to make one.',
      highlightSelector: '[data-guide="add-group"]',
      action: (s) => {
        s.openStudySettings('groups');
      },
      isReady: (s) => s.hasAnyGroup(),
      usesStudySettings: true,
    },
    {
      title: 'Add Students',
      description:
        'Your group is open — tap "+ Add student" and name your first ' +
        'student. For a child who is still learning, tick "Learning to ' +
        'read & write" to set how much they read and copy — you can ' +
        'adjust this anytime.',
      highlightSelector: '[data-guide="add-student"]',
      action: (s) => {
        s.openStudySettings('groups');
        s.expandNewestGroup();
      },
      isReady: (s) => s.hasStudentsInAnyGroup(),
      usesStudySettings: true,
    },
    {
      title: 'Teachers',
      description:
        'One teacher is always implied — your study prints a teacher sheet ' +
        'even with no teachers listed, so you can skip this step. ' +
        'If more than one person will teach, tap "+ Add teacher" so each ' +
        'gets their own sheet by name, with their own review questions.',
      highlightSelector: '[data-guide="add-teacher"]',
      action: (s) => {
        s.openStudySettings('groups');
      },
      usesStudySettings: true,
    },
    {
      title: 'Start a New Study',
      description:
        'Great — your group has students! Close the settings and tap the ' +
        '+ button at the top of the study panel to start building the study.',
      highlightSelector: '[data-guide="create-study"]',
      action: (s) => {
        s.closeStudySettings();
        s.showStudyPanel();
      },
      isReady: (s) => s.isCreatingStudy(),
    },
    {
      title: 'Building Your Study',
      highlightSelector: '[data-guide="starting-verse"]',
      description:
        'Your study is open in the editor — everything from here happens ' +
        'right there, and this card can stay open beside it for reference.\n\n' +
        '## Passage\n\n' +
        'Drag a starting verse from the Bible text into the Starting Verse ' +
        'box — and an ending verse to set a range. Writing Practice and ' +
        'Memory Verse take a drop the same way, for handwriting and ' +
        'memorization.\n\n' +
        'Name the study and pick your group.\n\n' +
        '## Scenes\n\n' +
        'Break the passage into scenes: a bold statement plus discussion ' +
        'points. Kids draw each scene, adults take notes.\n\n' +
        '## Assignments\n\n' +
        'Drop a verse on each student and add a review question suited to ' +
        'their age. For a student learning to read & write, the verse opens ' +
        'as tappable words — pick the word or words they will read and copy.\n\n' +
        '## Print\n\n' +
        'The print and download buttons in the editor header make the PDF ' +
        'worksheets — a sheet for every teacher and student. Go build your study!',
    },
  ];
}
